// ─── APP REDUCER ─────────────────────────────────────────────────────────────
// [ARCH-P4] v91: Centralized useReducer store.
// All state transitions go through here — atomic, predictable, testable.

import { createInitialState } from './initialState.js';
import { parseTaskContract }  from '../utils/dateEnforcement.js';
import * as A                 from './actions.js';

export function appReducer(state, action) {
  switch (action.type) {

    // ── Generic setters ────────────────────────────────────────────────────
    case A.SET_KEY:
      return {
        ...state,
        [action.key]: typeof action.value === 'function'
          ? action.value(state[action.key])
          : action.value,
      };

    // v96: Parse task text into contract on every keystroke
    case A.SET_TASK: {
      const taskText     = action.value || '';
      const taskContract = parseTaskContract(taskText);
      return { ...state, taskText, taskContract };
    }

    case A.SET_BATCH:
      return { ...state, ...action.payload };

    // ── Refs / File actions ────────────────────────────────────────────────

    // [SE-04] Atomic: change reference URL at index
    case A.SET_REFERENCE_URL_AND_INVALIDATE: {
      const urls = [...state.referenceUrls];
      urls[action.index] = action.value;
      return { ...state, referenceUrls: urls };
    }

    // Sitemap URL change
    case A.SET_SITEMAP_URL_AND_CLEAR:
      return { ...state, sitemapUrl: action.url };

    // ── Upload Bundle ───────────────────────────────────────────────────────

    case A.SET_UPLOAD_BUNDLE_LOADING:
      return {
        ...state,
        uploadBundle: { ...state.uploadBundle, isLoading: action.value, error: null },
      };

    case A.SET_UPLOAD_BUNDLE_FILES:
      return {
        ...state,
        uploadBundle: { ...state.uploadBundle, files: action.files, isLoading: false, error: null },
      };

    case A.SET_UPLOAD_BUNDLE_ERROR:
      return {
        ...state,
        uploadBundle: { ...state.uploadBundle, isLoading: false, error: action.error },
      };

    // ── Output / History ───────────────────────────────────────────────────

    // Atomic: clear content memory + editor state
    case A.CLEAR_CONTENT_MEMORY:
      return {
        ...state,
        contentMemory: [], editingEntryId: null,
        editedContent: {}, editValidationStatus: {},
      };

    // Atomic: delete history entry + cleanup editor + memory
    case A.DELETE_HISTORY_ENTRY: {
      const eid = action.entryId;
      const newEdited    = { ...state.editedContent };    delete newEdited[eid];
      const newValStatus = { ...state.editValidationStatus }; delete newValStatus[eid];
      return {
        ...state,
        outputHistory:         state.outputHistory.filter(e => e.id !== eid),
        contentMemory:         state.contentMemory.filter(m => m.id !== eid),
        editingEntryId:        state.editingEntryId === eid ? null : state.editingEntryId,
        editedContent:         newEdited,
        editValidationStatus:  newValStatus,
      };
    }

    // ── Full reset ─────────────────────────────────────────────────────────
    // isDarkMode is EXCLUDED — persists across resets.
    case A.FULL_RESET:
      return { ...createInitialState(), isDarkMode: state.isDarkMode };

    // ── Form reset ("Nuova generazione") ─────────────────────────────────────
    // Clears the brief + mode params + current outputs (back to step 1), but
    // PRESERVES the active tenant and the theme. Differs from FULL_RESET, which
    // resets `tenant` to the 'alens' default.
    case A.RESET_FORM:
      return {
        ...createInitialState(),
        isDarkMode: state.isDarkMode,
        tenant: state.tenant,
      };

    default:
      return state;
  }
}
